import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import adminApi from '../api/client';

// ── Types ────────────────────────────────────────────────────────────
interface DashboardStats {
  total_users: number;
  male_users?: number;
  female_users?: number;
  daily_active_users: number;
  new_today: number;
  active_sellers: number;
  flagged_content: number;
  suspended_users: number;
}

interface StatCard {
  label: string;
  key: keyof DashboardStats;
  icon: React.ReactNode;
  colorClasses: {
    bg: string;
    iconBg: string;
    text: string;
    border: string;
  };
  link?: string;
}

// ── Stat Card Definitions ────────────────────────────────────────────
const statCards: StatCard[] = [
  {
    label: 'Total Users',
    key: 'total_users',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    colorClasses: {
      bg: 'bg-blue-50',
      iconBg: 'bg-blue-100',
      text: 'text-blue-700',
      border: 'border-blue-100',
    },
  },
  {
    label: 'Daily Active Users',
    key: 'daily_active_users',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
      </svg>
    ),
    colorClasses: {
      bg: 'bg-green-50',
      iconBg: 'bg-green-100',
      text: 'text-green-700',
      border: 'border-green-100',
    },
  },
  {
    label: 'New Today',
    key: 'new_today',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
      </svg>
    ),
    colorClasses: {
      bg: 'bg-teal-50',
      iconBg: 'bg-teal-100',
      text: 'text-teal-700',
      border: 'border-teal-100',
    },
  },
  {
    label: 'Active Sellers',
    key: 'active_sellers',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z" />
      </svg>
    ),
    colorClasses: {
      bg: 'bg-amber-50',
      iconBg: 'bg-amber-100',
      text: 'text-amber-700',
      border: 'border-amber-100',
    },
  },
  {
    label: 'Flagged Content',
    key: 'flagged_content',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
      </svg>
    ),
    colorClasses: {
      bg: 'bg-red-50',
      iconBg: 'bg-red-100',
      text: 'text-red-700',
      border: 'border-red-100',
    },
    link: '/content',
  },
  {
    label: 'Suspended Users',
    key: 'suspended_users',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
      </svg>
    ),
    colorClasses: {
      bg: 'bg-orange-50',
      iconBg: 'bg-orange-100',
      text: 'text-orange-700',
      border: 'border-orange-100',
    },
  },
];

// ── Skeleton Card ────────────────────────────────────────────────────
const SkeletonCard: React.FC = () => (
  <div className="bg-white rounded-2xl p-6 border border-gray-100 animate-pulse">
    <div className="flex items-start justify-between">
      <div className="space-y-3 flex-1">
        <div className="h-4 w-24 bg-gray-200 rounded-lg" />
        <div className="h-9 w-20 bg-gray-200 rounded-lg" />
      </div>
      <div className="w-12 h-12 bg-gray-200 rounded-xl" />
    </div>
  </div>
);

// ── Dashboard Component ──────────────────────────────────────────────
const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await adminApi.get<DashboardStats>('/admin/dashboard');
        setStats(res.data);
      } catch (err: any) {
        setError('Failed to load dashboard stats.');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const formatNumber = (num: any): string => {
    if (num === undefined || num === null) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
  };

  const hasGenderCounts = typeof stats?.male_users === 'number' && typeof stats?.female_users === 'number';

  return (
    <div className="space-y-8">
      {/* Welcome section */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Welcome back 👋</h1>
          <p className="text-gray-500 text-sm mt-1">
            Here's what's happening with Readora today.
          </p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-100 shadow-sm">
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="text-xs font-medium text-gray-500">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </span>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="p-4 rounded-xl bg-coral/5 border border-coral/20 text-coral text-sm font-medium animate-slide-up">
          {error}
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {loading
          ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
          : statCards.map((card, idx) => {
            const value = stats ? stats[card.key] : 0;
            const isClickable = !!card.link;

            return (
              <div
                key={card.key}
                onClick={() => card.link && navigate(card.link)}
                className={`
                    group relative bg-white rounded-2xl p-6 border
                    ${card.colorClasses.border}
                    shadow-sm hover:shadow-md
                    transition-all duration-300
                    animate-slide-up
                    ${isClickable ? 'cursor-pointer hover:-translate-y-1' : ''}
                  `}
                style={{ animationDelay: `${idx * 60}ms`, animationFillMode: 'backwards' }}
              >
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-gray-500">
                      {card.label}
                    </p>
                    <p className={`text-3xl font-extrabold tracking-tight ${card.colorClasses.text}`}>
                      {card.key === 'total_users' && !hasGenderCounts ? 'Unavailable' : formatNumber(value)}
                    </p>
                    {card.key === 'total_users' && (
                      hasGenderCounts ? (
                        <>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                            <span>Male: {stats!.male_users!.toLocaleString()}</span>
                            <span>Female: {stats!.female_users!.toLocaleString()}</span>
                          </div>
                          <p className="text-xs text-gray-500">Not specified excluded</p>
                        </>
                      ) : (
                        <p className="text-xs text-gray-500">Gender counts unavailable.</p>
                      )
                    )}
                  </div>
                  <div className={`
                      w-12 h-12 rounded-xl flex items-center justify-center
                      ${card.colorClasses.iconBg} ${card.colorClasses.text}
                      group-hover:scale-110 transition-transform duration-300
                    `}>
                    {card.icon}
                  </div>
                </div>

                {/* Clickable indicator */}
                {isClickable && (
                  <div className="absolute bottom-3 right-4 flex items-center gap-1 text-xs font-medium text-gray-400 group-hover:text-gray-600 transition-colors">
                    <span>View</span>
                    <svg className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                )}

                {/* Decorative corner gradient */}
                <div className={`absolute top-0 right-0 w-20 h-20 rounded-tr-2xl rounded-bl-[40px] ${card.colorClasses.bg} opacity-50 -z-0`} />
              </div>
            );
          })}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
        <h3 className="text-lg font-bold text-gray-800 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Manage Users', icon: '👥', path: '/users', bg: 'bg-primary/5 hover:bg-primary/10 text-primary' },
            { label: 'Content Review', icon: '🚩', path: '/content', bg: 'bg-coral/5 hover:bg-coral/10 text-coral' },
            { label: 'Seller Panel', icon: '🏪', path: '/sellers', bg: 'bg-amber/5 hover:bg-amber/10 text-amber' },
            { label: 'View Analytics', icon: '📈', path: '/analytics', bg: 'bg-accent/5 hover:bg-accent/10 text-accent' },
          ].map((action) => (
            <button
              key={action.path}
              onClick={() => navigate(action.path)}
              className={`
                flex items-center gap-3 px-4 py-3.5 rounded-xl
                ${action.bg}
                font-semibold text-sm
                transition-all duration-200
                active:scale-[0.97]
              `}
            >
              <span className="text-xl">{action.icon}</span>
              {action.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
